// PasskeyManager Component
// Main component for managing user passkeys

import React, { useState, useEffect } from 'react';
import { usePasskey } from '../hooks/usePasskey';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert } from './ui/alert';
import { 
  Key, 
  Plus, 
  Trash2, 
  Edit3, 
  Shield, 
  Smartphone, 
  Monitor, 
  Usb, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Fingerprint
} from 'lucide-react';
import type { PasskeyManagerProps, PasskeyListItem } from '../types/webauthn';

const PasskeyManager: React.FC<PasskeyManagerProps> = ({
  userId,
  onRegistrationSuccess,
  onAuthenticationSuccess,
  onError,
  className = '',
  showAdvanced = false
}) => {
  const {
    isSupported,
    isLoading,
    credentials,
    error,
    register,
    authenticate,
    deleteCredential,
    updateCredential,
    refreshCredentials,
    clearError,
    canRegister,
    hasConditionalMediation
  } = usePasskey(userId);

  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [editingCredential, setEditingCredential] = useState<string | null>(null);
  const [newCredentialName, setNewCredentialName] = useState('');
  const [editCredentialName, setEditCredentialName] = useState('');

  // Handle errors
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Handle registration
  const handleRegister = async () => {
    if (!userId) return;

    const success = await register({
      user_id: userId,
      username: `user_${userId}`,
      display_name: 'Saúde Virtual User',
      credential_name: newCredentialName || 'Novo Passkey',
      authenticator_attachment: 'platform',
      user_verification: 'preferred',
      resident_key: 'preferred'
    });

    if (success) {
      setShowRegistrationForm(false);
      setNewCredentialName('');
      if (onRegistrationSuccess && credentials.length > 0) {
        // This is a simplified callback - in real implementation you'd pass the actual credential
        onRegistrationSuccess(credentials[0] as any);
      }
    }
  };

  // Handle authentication test
  const handleAuthenticate = async () => {
    const success = await authenticate({ user_id: userId });
    
    if (success && onAuthenticationSuccess && credentials.length > 0) {
      // This is a simplified callback - in real implementation you'd pass the actual credential
      onAuthenticationSuccess(credentials[0] as any);
    }
  };

  // Handle credential deletion
  const handleDelete = async (credentialId: string) => {
    if (confirm('Tem certeza que deseja remover este passkey?')) {
      await deleteCredential(credentialId);
    }
  };

  // Handle credential name update
  const handleUpdateName = async (credentialId: string) => {
    if (editCredentialName.trim()) {
      const success = await updateCredential(credentialId, editCredentialName.trim());
      if (success) {
        setEditingCredential(null);
        setEditCredentialName('');
      }
    }
  };

  // Start editing credential name
  const startEditing = (credential: PasskeyListItem) => {
    setEditingCredential(credential.id);
    setEditCredentialName(credential.name);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingCredential(null);
    setEditCredentialName('');
  };

  // Get device icon based on transport
  const getDeviceIcon = (transports: string[], deviceType: string) => {
    if (transports.includes('internal') || deviceType === 'single_device') {
      return <Smartphone className="h-4 w-4" />;
    }
    if (transports.includes('usb')) {
      return <Usb className="h-4 w-4" />;
    }
    return <Monitor className="h-4 w-4" />;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isSupported) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">WebAuthn não suportado</h3>
          <p className="text-gray-600 mb-4">
            Seu navegador não suporta passkeys. Para usar esta funcionalidade, 
            atualize para uma versão mais recente do Chrome, Firefox, Safari ou Edge.
          </p>
          <Badge variant="outline" className="text-yellow-600">
            Funcionalidade indisponível
          </Badge>
        </div>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Key className="h-6 w-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold">Gerenciar Passkeys</h2>
            <p className="text-sm text-gray-600">
              Gerencie seus passkeys para autenticação segura
            </p>
          </div>
        </div>
        
        {hasConditionalMediation() && (
          <Badge variant="outline" className="text-green-600">
            <Fingerprint className="h-3 w-3 mr-1" />
            Preenchimento automático
          </Badge>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <div>
            <h4 className="font-semibold">{error.name}</h4>
            <p>{error.message}</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={clearError}
            >
              Fechar
            </Button>
          </div>
        </Alert>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {canRegister() && (
          <Button
            onClick={() => setShowRegistrationForm(true)}
            disabled={isLoading || !userId}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Adicionar Passkey</span>
          </Button>
        )}
        
        {credentials.length > 0 && (
          <Button
            variant="outline"
            onClick={handleAuthenticate}
            disabled={isLoading}
            className="flex items-center space-x-2"
          >
            <Shield className="h-4 w-4" />
            <span>Testar Autenticação</span>
          </Button>
        )}
        
        <Button
          variant="outline"
          onClick={refreshCredentials}
          disabled={isLoading}
          className="flex items-center space-x-2"
        >
          <span>Atualizar</span>
        </Button>
      </div>

      {/* Registration Form */}
      {showRegistrationForm && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Adicionar Novo Passkey</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Nome do Passkey (opcional)
              </label>
              <Input
                value={newCredentialName}
                onChange={(e) => setNewCredentialName(e.target.value)}
                placeholder="Ex: iPhone, YubiKey, Windows Hello"
                className="w-full"
              />
            </div>
            
            <div className="flex space-x-3">
              <Button
                onClick={handleRegister}
                disabled={isLoading}
                className="flex items-center space-x-2"
              >
                <Key className="h-4 w-4" />
                <span>Criar Passkey</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  setShowRegistrationForm(false);
                  setNewCredentialName('');
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Credentials List */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Seus Passkeys ({credentials.length})</h3>
        
        {credentials.length === 0 ? (
          <Card className="p-6 text-center">
            <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-600 mb-2">
              Nenhum passkey configurado
            </h4>
            <p className="text-gray-500 mb-4">
              Adicione um passkey para autenticação rápida e segura
            </p>
            {canRegister() && (
              <Button
                onClick={() => setShowRegistrationForm(true)}
                disabled={!userId}
              >
                Adicionar Primeiro Passkey
              </Button>
            )}
          </Card>
        ) : (
          credentials.map((credential) => (
            <Card key={credential.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getDeviceIcon(credential.transports, credential.device_type)}
                  
                  <div className="flex-1">
                    {editingCredential === credential.id ? (
                      <div className="flex items-center space-x-2">
                        <Input
                          value={editCredentialName}
                          onChange={(e) => setEditCredentialName(e.target.value)}
                          className="flex-1"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateName(credential.credential_id);
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={() => handleUpdateName(credential.credential_id)}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEditing}
                        >
                          ✕
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <h4 className="font-semibold">{credential.name}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>Criado: {formatDate(credential.created_at)}</span>
                          </span>
                          {credential.last_used_at && (
                            <span className="flex items-center space-x-1">
                              <CheckCircle className="h-3 w-3" />
                              <span>Último uso: {formatDate(credential.last_used_at)}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Badge 
                    variant={credential.device_type === 'single_device' ? 'default' : 'secondary'}
                  >
                    {credential.device_type === 'single_device' ? 'Dispositivo' : 'Portátil'}
                  </Badge>
                  
                  {editingCredential !== credential.id && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(credential)}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(credential.credential_id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {showAdvanced && (
                <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                  <div className="grid grid-cols-2 gap-2">
                    <span>ID: {credential.credential_id.substring(0, 16)}...</span>
                    <span>Transportes: {credential.transports.join(', ')}</span>
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Processando operação...</p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PasskeyManager;